import { motion } from 'framer-motion'
import { useSearchParams } from 'react-router-dom'
import Link from '../components/LocalizedLink'
import { Download as DownloadIcon, Check, AlertCircle, HardDrive, Monitor, Cpu, BookOpen, FileText, Wrench, Smartphone, Package } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'
import Turnstile from '../components/Turnstile'

const BASE = import.meta.env.BASE_URL

const docs = {
  en: [
    {
      category: 'User Manual', categoryIcon: <BookOpen className="h-5 w-5" />,
      items: [
        { title: 'Sheet Metal Estimator — User Manual', lang: 'EN', file: 'Sheet_Metal_Estimator_User_Manual_EN.pdf', description: 'Complete guide to using Workshop Assistant: calculator, import, offers, parameters.' },
        { title: 'Εγχειρίδιο Χρήστη — Υπολογιστής Κόστους', lang: 'GR', file: 'Sheet_Metal_Estimator_User_Manual_EL.pdf', description: 'Πλήρης οδηγός χρήσης: υπολογιστής, εισαγωγή, προσφορές, παράμετροι.' },
      ],
    },
    {
      category: 'CAD Plugin — Installation', categoryIcon: <Wrench className="h-5 w-5" />,
      items: [
        { title: 'SheetMetalToJSON — Installation Guide', lang: 'EN', file: 'SheetMetalToJSON_Installation_Guide_EN.pdf', description: 'How to install and configure the SheetMetalToJSON CAD plugin.' },
        { title: 'SheetMetalToJSON — Οδηγός Εγκατάστασης', lang: 'GR', file: 'SheetMetalToJSON_Installation_Guide_GR.pdf', description: 'Εγκατάσταση και ρύθμιση του plugin SheetMetalToJSON για CAD.' },
        { title: 'SheetMetalToJSON — SolidWorks Guide', lang: 'EN', file: 'SheetMetalToJSON_SolidWorks_Installation_Guide_EN.pdf', description: 'Step-by-step SolidWorks add-in setup for metadata export.' },
        { title: 'SheetMetalToJSON — Οδηγός SolidWorks', lang: 'GR', file: 'SheetMetalToJSON_SolidWorks_Installation_Guide_GR.pdf', description: 'Ρύθμιση add-in SolidWorks για εξαγωγή μεταδεδομένων.' },
      ],
    },
  ],
  el: [
    {
      category: 'Εγχειρίδιο Χρήστη', categoryIcon: <BookOpen className="h-5 w-5" />,
      items: [
        { title: 'Εγχειρίδιο Χρήστη — Υπολογιστής Κόστους', lang: 'GR', file: 'Sheet_Metal_Estimator_User_Manual_EL.pdf', description: 'Πλήρης οδηγός χρήσης: υπολογιστής, εισαγωγή, προσφορές, παράμετροι.' },
        { title: 'Sheet Metal Estimator — User Manual', lang: 'EN', file: 'Sheet_Metal_Estimator_User_Manual_EN.pdf', description: 'Complete guide to using Workshop Assistant: calculator, import, offers, parameters.' },
      ],
    },
    {
      category: 'Plugin CAD — Εγκατάσταση', categoryIcon: <Wrench className="h-5 w-5" />,
      items: [
        { title: 'SheetMetalToJSON — Οδηγός Εγκατάστασης', lang: 'GR', file: 'SheetMetalToJSON_Installation_Guide_GR.pdf', description: 'Εγκατάσταση και ρύθμιση του plugin SheetMetalToJSON για CAD.' },
        { title: 'SheetMetalToJSON — Installation Guide', lang: 'EN', file: 'SheetMetalToJSON_Installation_Guide_EN.pdf', description: 'How to install and configure the SheetMetalToJSON CAD plugin.' },
        { title: 'SheetMetalToJSON — Οδηγός SolidWorks', lang: 'GR', file: 'SheetMetalToJSON_SolidWorks_Installation_Guide_GR.pdf', description: 'Ρύθμιση add-in SolidWorks για εξαγωγή μεταδεδομένων.' },
        { title: 'SheetMetalToJSON — SolidWorks Guide', lang: 'EN', file: 'SheetMetalToJSON_SolidWorks_Installation_Guide_EN.pdf', description: 'Step-by-step SolidWorks add-in setup for metadata export.' },
      ],
    },
  ],
}

const translations = {
  el: {
    hero: {
      title: 'Κατεβάστε το Workshop Assistant',
      subtitle: 'Ξεκινήστε τη δωρεάν δοκιμή σήμερα. Δεν απαιτείται πιστωτική κάρτα.',
      features: ['5 ημέρες δωρεάν δοκιμή', 'Πλήρης πρόσβαση', 'Εισαγωγή DXF & STEP', 'Δημιουργία αναφορών Excel', 'Χωρίς πιστωτική κάρτα', 'Άμεση ενεργοποίηση'],
    },
    form: {
      title: 'Δωρεάν Δοκιμή',
      emailLabel: 'Διεύθυνση Email *',
      emailPlaceholder: 'το@email.σας',
      emailHint: 'Απαιτείται για τη λήψη. Λαμβάνετε επίσης ενημερώσεις, συμβουλές και αποκλειστικές προσφορές',
      emailError: 'Παρακαλώ εισάγετε μια έγκυρη διεύθυνση email',
      versionLabel: 'Επιλέξτε Έκδοση',
      versionName: 'Windows 10/11 (64-bit)',
      versionDesc: 'Προτεινόμενο',
      androidName: 'ShearNesting (Android)',
      androidDesc: 'Εφαρμογή APK για Android',
      servicePackName: 'Service Pack (Windows)',
      servicePackDesc: 'FreeCAD, ODA File Converter & στοιχεία Python — για νέους υπολογιστές',
      termsText: 'Συμφωνώ με τους',
      termsLink: 'Όρους Χρήσης',
      andText: 'και την',
      privacyLink: 'Πολιτική Απορρήτου',
      btn: 'Λήψη Τώρα (Δωρεάν)',
      fileSizeLabel: 'Μέγεθος αρχείου',
      instant: 'Άμεση λήψη',
      termsError: 'Παρακαλώ αποδεχτείτε τους όρους χρήσης',
      sending: 'Αποστολή...',
      confirmMsg: 'Σχεδόν έτοιμοι! Ελέγξτε το email σας και κάντε κλικ στον σύνδεσμο επιβεβαίωσης για να ξεκινήσει η λήψη.',
      requestError: 'Κάτι πήγε στραβά. Παρακαλώ δοκιμάστε ξανά.',
      linkExpired: 'Ο σύνδεσμος επιβεβαίωσης έληξε. Παρακαλώ ζητήστε ξανά τη λήψη.',
      captchaError: 'Παρακαλώ ολοκληρώστε την επαλήθευση ασφαλείας.',
      guideEmail: 'Βήμα 1 από 4 — Εισάγετε το email σας για να συνεχίσετε',
      guideVersion: 'Βήμα 2 από 4 — Επιλέξτε την έκδοση που θέλετε',
      guideAgree: 'Βήμα 3 από 4 — Αποδεχτείτε τους όρους',
      guideDownload: 'Βήμα 4 από 4 — Πατήστε λήψη για να ξεκινήσετε!',
    },
    note: 'Σημείωση: Τα Windows ενδέχεται να εμφανίσουν προειδοποίηση ασφαλείας. Κάντε κλικ στο "Περισσότερες πληροφορίες" και μετά "Εκτέλεση ούτως ή άλλως" για να προχωρήσετε.',
    docsTitle: 'Τεκμηρίωση & Οδηγοί',
    docsSubtitle: 'Όλα όσα χρειάζεστε για να ξεκινήσετε και να αξιοποιήσετε στο έπακρο το Workshop Assistant.',
    downloadPdf: 'Λήψη PDF',
    requirements: {
      title: 'Απαιτήσεις Συστήματος',
      items: ['Windows 10 ή νεότερο', '4GB RAM (8GB συνιστάται)', '500MB χώρος δίσκου'],
    },
    steps: {
      title: 'Βήματα Εγκατάστασης',
      items: [
        { title: 'Κατεβάστε το πρόγραμμα εγκατάστασης', description: 'Κάντε κλικ στο κουμπί λήψης παραπάνω για να αποκτήσετε την τελευταία έκδοση.' },
        { title: 'Εκτελέστε το πρόγραμμα εγκατάστασης', description: 'Κάντε διπλό κλικ στο αρχείο που κατεβάσατε για να ξεκινήσει η εγκατάσταση.' },
        { title: 'Ακολουθήστε τον οδηγό', description: 'Ο οδηγός εγκατάστασης θα σας καθοδηγήσει σε όλη τη διαδικασία.' },
        { title: 'Εκκινήστε το Workshop Assistant', description: 'Ανοίξτε την εφαρμογή και ξεκινήστε να δημιουργείτε την πρώτη σας προσφορά!' },
      ],
    },
    help: { title: 'Χρειάζεστε Βοήθεια;', subtitle: 'Ελέγξτε την τεκμηρίωσή μας ή επικοινωνήστε με την ομάδα μας.', docs: 'Δείτε Τεκμηρίωση', contact: 'Επικοινωνία' },
  },
  en: {
    hero: {
      title: 'Download Workshop Assistant',
      subtitle: 'Start your free trial today. No credit card required.',
      features: ['5-day free trial', 'Full feature access', 'DXF & STEP import', 'Excel report generation', 'No credit card required', 'Instant activation'],
    },
    form: {
      title: 'Download Free Trial',
      emailLabel: 'Email Address *',
      emailPlaceholder: 'your@email.com',
      emailHint: 'Required to download. You\'ll also receive updates, tips, and exclusive offers',
      emailError: 'Please enter a valid email address',
      versionLabel: 'Select Version',
      versionName: 'Windows 10/11 (64-bit)',
      versionDesc: 'Recommended',
      androidName: 'ShearNesting (Android)',
      androidDesc: 'Android APK app',
      servicePackName: 'Service Pack (Windows)',
      servicePackDesc: 'FreeCAD, ODA File Converter & Python components — for new PCs',
      termsText: 'I agree to the',
      termsLink: 'Terms of Service',
      andText: 'and',
      privacyLink: 'Privacy Policy',
      btn: 'Download Now (Free)',
      fileSizeLabel: 'File size',
      instant: 'Instant download',
      termsError: 'Please agree to the terms and conditions',
      sending: 'Sending...',
      confirmMsg: 'Almost there! Check your email and click the confirmation link to start your download.',
      requestError: 'Something went wrong. Please try again.',
      linkExpired: 'That confirmation link has expired. Please request the download again.',
      captchaError: 'Please complete the security verification.',
      guideEmail: 'Step 1 of 4 — Enter your email to continue',
      guideVersion: 'Step 2 of 4 — Select your version',
      guideAgree: 'Step 3 of 4 — Accept the terms',
      guideDownload: 'Step 4 of 4 — Hit download to get started!',
    },
    note: 'Note: Windows may show a security warning because the app is not yet widely distributed. Click "More info" and then "Run anyway" to proceed with installation.',
    docsTitle: 'Documentation & Guides',
    docsSubtitle: 'Everything you need to get started and get the most out of Workshop Assistant.',
    downloadPdf: 'Download PDF',
    requirements: {
      title: 'System Requirements',
      items: ['Windows 10 or later', '4GB RAM (8GB recommended)', '500MB disk space'],
    },
    steps: {
      title: 'Installation Steps',
      items: [
        { title: 'Download the installer', description: 'Click the download button above to get the latest version.' },
        { title: 'Run the installer', description: 'Double-click the downloaded file to start installation.' },
        { title: 'Follow the wizard', description: 'The installation wizard will guide you through the process.' },
        { title: 'Launch Workshop Assistant', description: 'Open the application and start creating your first offer!' },
      ],
    },
    help: { title: 'Need Help?', subtitle: 'Check our support documentation or contact our team for assistance.', docs: 'View Documentation', contact: 'Contact Support' },
  },
}

const requirementIcons = [<Monitor className="h-6 w-6" />, <Cpu className="h-6 w-6" />, <HardDrive className="h-6 w-6" />]

const Download = () => {
  const [email, setEmail] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [version, setVersion] = useState('windows')
  const [versionTouched, setVersionTouched] = useState(false)
  const [website, setWebsite] = useState('') // honeypot — real users never fill this
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [confirmSent, setConfirmSent] = useState(false)
  const [latestVersion, setLatestVersion] = useState(null)
  const [fileSizes, setFileSizes] = useState({})
  const { lang } = useLanguage()
  const t = translations[lang]
  const currentDocs = docs[lang]
  const [searchParams, setSearchParams] = useSearchParams()

  const emailValid = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email.trim())

  // Guided flow: email -> version -> agree to terms -> download button
  const guideStep = !emailValid ? 1 : !versionTouched ? 2 : !agreed ? 3 : 4
  const guideText = { 1: t.form.guideEmail, 2: t.form.guideVersion, 3: t.form.guideAgree, 4: t.form.guideDownload }[guideStep]
  const highlight = 'ring-4 ring-primary-400 ring-offset-2 rounded-lg'

  const versionStepRef = useRef(null)
  const agreeStepRef = useRef(null)
  const downloadBtnRef = useRef(null)
  const prevGuideStep = useRef(guideStep)

  // Keep the newly-active step in view as the guide advances (email step is
  // already brought into view by ScrollToTop on page load, so skip step 1)
  useEffect(() => {
    if (guideStep !== prevGuideStep.current) {
      const targetRef = { 2: versionStepRef, 3: agreeStepRef, 4: downloadBtnRef }[guideStep]
      targetRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      prevGuideStep.current = guideStep
    }
  }, [guideStep])

  useEffect(() => {
    fetch(`${BASE}api/version/latest`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data?.version) setLatestVersion(data.version) })
      .catch(() => {})
    fetch(`${BASE}api/download-sizes`)
      .then((r) => (r.ok ? r.json() : {}))
      .then(setFileSizes)
      .catch(() => {})
  }, [])

  useEffect(() => {
    const confirm = searchParams.get('confirm')
    if (confirm === 'expired') toast.error(t.form.linkExpired)
    else if (confirm === 'error') toast.error(t.form.requestError)
    if (confirm) setSearchParams({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDownload = async (e) => {
    e.preventDefault()
    if (!emailValid) { toast.error(t.form.emailError); return }
    if (!agreed) { toast.error(t.form.termsError); return }
    if (!turnstileToken) { toast.error(t.form.captchaError); return }

    setIsSubmitting(true)
    try {
      // Service pack has its own untracked endpoint (no lead email / access log)
      const endpoint = version === 'servicepack' ? 'api/servicepack/request' : 'api/request-download'
      const response = await fetch(`${import.meta.env.BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, version, website, turnstileToken })
      })
      if (!response.ok) throw new Error('Failed')
      toast.success(t.form.confirmMsg)
      setConfirmSent(true)
    } catch {
      toast.error(t.form.requestError)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="overflow-hidden">
      <SEO
        title="Download Free Trial"
        description="Download Workshop Assistant free — no credit card required. Windows and Android versions available with instant activation."
        path="/download"
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="inline-flex items-center justify-center w-24 h-24 bg-white bg-opacity-20 rounded-full mb-8">
              <DownloadIcon className="h-12 w-12" />
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90 mb-4">{t.hero.subtitle}</motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="flex flex-wrap justify-center gap-4 text-sm">
              {t.hero.features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2"><Check className="h-5 w-5" /><span>{feature}</span></div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Download Form */}
      <section id="download-form" className="section-container bg-white scroll-mt-24">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-4 text-center">{t.form.title}</h2>
            {!confirmSent && (
              <motion.div
                key={guideStep}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 flex items-center justify-center gap-2 bg-primary-50 border border-primary-200 text-primary-700 text-sm font-medium rounded-lg py-2 px-4 text-center"
              >
                {guideText}
              </motion.div>
            )}
            <form onSubmit={handleDownload} className="space-y-6">
              <div className={`transition-all duration-300 ${guideStep === 1 ? highlight : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t.form.emailLabel}</label>
                <input type="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.form.emailPlaceholder} className="input-field" />
                <p className="text-sm text-gray-500 mt-2">{t.form.emailHint}</p>
              </div>
              <div ref={versionStepRef} className={`transition-all duration-300 ${guideStep === 2 ? highlight : ''} ${guideStep < 2 ? 'opacity-50' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t.form.versionLabel}</label>
                <div className="space-y-3">
                  <label onClick={() => setVersionTouched(true)} className={`card flex items-center justify-between cursor-pointer border-2 ${version === 'windows' ? 'border-primary-500' : 'border-gray-200 hover:border-primary-500'}`}>
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center"><Monitor className="h-6 w-6 text-primary-600" /></div>
                      <div><div className="font-semibold">{t.form.versionName}</div><div className="text-sm text-gray-500">{t.form.versionDesc}{latestVersion && ` - v${latestVersion}`}</div></div>
                    </div>
                    <input type="radio" name="version" value="windows" checked={version === 'windows'} onChange={() => setVersion('windows')} className="h-5 w-5 text-primary-600" />
                  </label>
                  <label onClick={() => setVersionTouched(true)} className={`card flex items-center justify-between cursor-pointer border-2 ${version === 'android' ? 'border-primary-500' : 'border-gray-200 hover:border-primary-500'}`}>
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center"><Smartphone className="h-6 w-6 text-primary-600" /></div>
                      <div><div className="font-semibold">{t.form.androidName}</div><div className="text-sm text-gray-500">{t.form.androidDesc}</div></div>
                    </div>
                    <input type="radio" name="version" value="android" checked={version === 'android'} onChange={() => setVersion('android')} className="h-5 w-5 text-primary-600" />
                  </label>
                  <label onClick={() => setVersionTouched(true)} className={`card flex items-center justify-between cursor-pointer border-2 ${version === 'servicepack' ? 'border-primary-500' : 'border-gray-200 hover:border-primary-500'}`}>
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center"><Package className="h-6 w-6 text-primary-600" /></div>
                      <div><div className="font-semibold">{t.form.servicePackName}</div><div className="text-sm text-gray-500">{t.form.servicePackDesc}</div></div>
                    </div>
                    <input type="radio" name="version" value="servicepack" checked={version === 'servicepack'} onChange={() => setVersion('servicepack')} className="h-5 w-5 text-primary-600" />
                  </label>
                </div>
              </div>
              <div ref={agreeStepRef} className={`bg-gray-50 rounded-lg p-4 transition-all duration-300 ${guideStep === 3 ? highlight : ''} ${guideStep < 3 ? 'opacity-50' : ''}`}>
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 h-5 w-5 text-primary-600 rounded" />
                  <span className="text-sm text-gray-700">
                    {t.form.termsText}{' '}<Link to="/terms" className="text-primary-600 hover:underline">{t.form.termsLink}</Link>{' '}{t.form.andText}{' '}<Link to="/privacy" className="text-primary-600 hover:underline">{t.form.privacyLink}</Link>
                  </span>
                </label>
              </div>
              {/* Honeypot: hidden from real users, catches bots that fill every field */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
              />
              {confirmSent ? (
                <div className="w-full bg-green-50 border border-green-200 rounded-lg py-4 px-6 text-center text-green-800 font-medium">
                  {t.form.confirmMsg}
                </div>
              ) : (
                <>
                  <div className="flex justify-center">
                    <Turnstile onVerify={setTurnstileToken} />
                  </div>
                  <button
                    ref={downloadBtnRef}
                    type="submit"
                    disabled={!agreed || !emailValid || !turnstileToken || isSubmitting}
                    className={`w-full btn-primary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-all duration-300 ${guideStep === 4 && turnstileToken ? 'ring-4 ring-primary-400 ring-offset-2 animate-pulse' : ''}`}
                  >
                    {isSubmitting ? <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /><span>{t.form.sending}</span></> : <><DownloadIcon className="h-6 w-6" /><span>{t.form.btn}</span></>}
                  </button>
                </>
              )}
              <p className="text-center text-sm text-gray-500">{fileSizes[version] ? `${t.form.fileSizeLabel}: ${Math.round(fileSizes[version] / (1024 * 1024))} MB · ${t.form.instant}` : t.form.instant}</p>
            </form>
            <div className="mt-8 p-4 bg-blue-50 rounded-lg flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">{t.note}</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Documentation */}
      <section className="section-container bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">{t.docsTitle}</h2>
            <p className="text-gray-600 text-lg">{t.docsSubtitle}</p>
          </div>
          <div className="space-y-10">
            {currentDocs.map((group, gi) => (
              <motion.div key={gi} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: gi * 0.1 }}>
                <div className="flex items-center space-x-2 mb-4">
                  <div className="text-primary-600">{group.categoryIcon}</div>
                  <h3 className="text-lg font-semibold text-gray-800">{group.category}</h3>
                  <div className="flex-grow h-px bg-gray-200 ml-2" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {group.items.map((doc, di) => (
                    <div key={di} className="bg-white rounded-xl border border-gray-200 p-5 flex items-start space-x-4 shadow-sm hover:shadow-md hover:border-primary-300 transition-all duration-200">
                      <div className="flex-shrink-0 w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                        <FileText className="h-6 w-6 text-red-500" />
                      </div>
                      <div className="flex-grow min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <span className="font-semibold text-gray-900 text-sm leading-snug">{doc.title}</span>
                          <span className={`flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${doc.lang === 'EN' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{doc.lang}</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-3 leading-relaxed">{doc.description}</p>
                        <a href={`${BASE}Documentation/${doc.file}`} download={doc.file} className="inline-flex items-center space-x-1.5 text-sm font-medium text-primary-600 hover:text-primary-800 transition-colors">
                          <DownloadIcon className="h-4 w-4" /><span>{t.downloadPdf}</span>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* System Requirements */}
      <section className="section-container bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">{t.requirements.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.requirements.items.map((req, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="card text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-600 rounded-full mb-4">{requirementIcons[index]}</div>
                <div className="font-semibold">{req}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Steps */}
      <section className="section-container bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">{t.steps.title}</h2>
          <div className="space-y-6">
            {t.steps.items.map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="flex items-start space-x-6">
                <div className="flex-shrink-0 w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-xl">{index + 1}</div>
                <div className="flex-grow">
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Help */}
      <section className="gradient-primary text-white">
        <div className="section-container max-w-4xl text-center">
          <h2 className="text-4xl font-bold mb-6">{t.help.title}</h2>
          <p className="text-xl mb-8 opacity-90">{t.help.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/support" className="btn-secondary bg-white text-primary-600 hover:bg-gray-100">{t.help.docs}</Link>
            <Link to="/contact" className="btn-outline border-white text-white hover:bg-white hover:text-primary-600">{t.help.contact}</Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Download
